import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ShieldOff, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';

interface StudentAccessStatus {
  isActive: boolean;
  accessRevoked: boolean;
  accessRevokedReason?: string;
  isArchived: boolean;
}

/**
 * Component to display access restriction messages for students
 * Shows warnings when:
 * - Access is revoked (fee non-payment) - can see dashboard but not tests/analytics
 * - Account is deactivated - should not be able to login (handled by auth)
 * - Account is archived - should not be able to login (handled by auth)
 */
export const StudentAccessBanner: React.FC = () => {
  const [accessStatus, setAccessStatus] = useState<StudentAccessStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAccessStatus();
  }, []);

  const checkAccessStatus = async () => {
    try {
      // Get current user profile which should include access status
      const response = await api.get('/auth/me');
      const userData = response.data;

      setAccessStatus({
        isActive: userData.isActive !== false, // Default to true if not specified
        accessRevoked: userData.accessRevoked === true,
        accessRevokedReason: userData.accessRevokedReason,
        isArchived: userData.isArchived === true
      });
    } catch (error) {
      console.error('Failed to check access status:', error);
      // On error, assume normal access to avoid blocking users unnecessarily
      setAccessStatus({
        isActive: true,
        accessRevoked: false,
        isArchived: false
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !accessStatus) {
    return null;
  }

  // Show banner if access is revoked
  if (accessStatus.accessRevoked) {
    const reason = accessStatus.accessRevokedReason || 'Fee payment pending';
    
    return (
      <Alert variant="destructive" className="mb-4">
        <ShieldOff className="h-4 w-4" />
        <AlertTitle>Access Temporarily Suspended</AlertTitle>
        <AlertDescription>
          Your access to tests and analytics has been temporarily suspended.
          <br />
          <strong>Reason:</strong> {reason}
          <br />
          <span className="text-sm">
            You can still view your dashboard, but cannot start new tests or view analytics.
            Please contact administration to resolve this issue.
          </span>
        </AlertDescription>
      </Alert>
    );
  }

  // If account is inactive or archived (shouldn't reach here as auth should block)
  if (!accessStatus.isActive || accessStatus.isArchived) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Account Inactive</AlertTitle>
        <AlertDescription>
          Your account is currently inactive. Please contact administration for assistance.
        </AlertDescription>
      </Alert>
    );
  }

  return null;
};

/**
 * Hook to check if student has access to specific features
 */
export const useStudentAccess = () => {
  const [hasFullAccess, setHasFullAccess] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const response = await api.get('/auth/me');
      const userData = response.data;

      const isActive = userData.isActive !== false;
      const accessRevoked = userData.accessRevoked === true;
      const isArchived = userData.isArchived === true;

      // Has full access only if active, not revoked, and not archived
      setHasFullAccess(isActive && !accessRevoked && !isArchived);
    } catch (error) {
      console.error('Failed to check access:', error);
      // On error, assume full access to avoid blocking users
      setHasFullAccess(true);
    } finally {
      setLoading(false);
    }
  };

  return { hasFullAccess, loading, refreshAccess: checkAccess };
};
